import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Title, Code, Actions, API, Utility, Button, Messages, Control } from 'helpers/PrintImports';
import { Client } from 'models/ClientModel';
import SignaturePad from "components/common/SignManager";
import { useSearchParams } from 'react-router-dom';

const ConsentPrint11: React.FC = () => {
  const titleName = 'Patient Consent Form';
  const [formId, formName] = [Code.Form.Id.CLIENT, Code.Form.Name.CLIENT];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [clientData, setClientData] = useState<Client>({});
  const [signData, setSignData] = useState<string>('');
  const [receiverName, setReceiverName] = useState<string>('');
  const [receiverPhone, setReceiverPhone] = useState<string>('');

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const today = Utility.Date.Current();

  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);
  const companyName = Utility.Storage.CompanyName();
  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  

  useEffect(() => {
    if(!clientId) return;
    API.Data.GetByKeyId<Client>(formName, clientId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setClientData((prevData) => ({
        ...prevData,
        ...formData,
      }));
      setReceiverName(Utility.Get.FullName(data,Code.Form.Name.CLIENT))
      setReceiverPhone(data.client_phone || '')
    }})
    setLoading(false);
  }, []); 

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={'FORM'} Privilege={Privilege} clientData={clientData} fileName={titleName} docType={Code.Option.DocumentType.CONSENT_FORM} printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
            <Control.Flex>
              <Control.Column size={[0,60,0,0]}>
                <Control.Box>
                  <Field.CompanyHead companyId={companyId} locationId={locationId} />
                </Control.Box>
                <Control.Box>
                  <Field.Value value={Utility.Get.FullName(clientData, Code.Form.Name.CLIENT).toUpperCase()} valueBold={true} />
                  <Field.Value value={Utility.Format.Phone(clientData?.client_phone)} />
                  <Field.Value value={`${clientData?.client_address} ${clientData?.client_postal}`} />
                  <Field.Value title={Title.DOB} value={Utility.Format.DateOnly(clientData?.client_dob)} />
                  <Field.Value title={Title.ClientId} value={`#${clientData.client_number}`} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,40,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.SubHead title={titleName} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <div className="content-consent pt8">
              <p><strong>Consent for Eye Examination, Health Record Storage &amp; Communication</strong></p>
              <p className='tjust'>I, the undersigned, consent to undergo a comprehensive eye examination at {companyName}. I understand that the examination is intended to assess my eye health and vision and to provide recommendations for corrective lenses or treatment as needed. I acknowledge that {companyName} does not guarantee any diagnosis, outcome, or results.</p>
              <p className='tjust'>I consent to the collection, use, and electronic storage of my personal and health information, including my name, Alberta Health Care Number, date of birth, contact information, medical and eye health history, examination results, photographs, and signatures. This information may be securely stored in a cloud-based practice management system, which may include servers located in Canada. {companyName} will take reasonable measures to protect my information but cannot guarantee absolute security.</p>
              <p className='tjust'>I understand that my information may be used to provide care, maintain records, manage appointments, and ensure continuity of services. I consent to being contacted by phone, text message (SMS), email, or other electronic communication for appointment reminders, test results, follow-ups, clinic updates, links to forms or instructions, and other communications related to my eye care. I also consent to receiving promotional offers, newsletters, or marketing campaigns, and understand that I may opt out of such communications at any time.</p>
              <p className='tjust'>{companyName} will implement reasonable safeguards in accordance with the Alberta Health Information Act (HIA) and applicable privacy regulations. My information may be shared with authorized third-party applications, healthcare providers, and service partners involved in delivering my care, or disclosed as required by applicable law. Any legal matters arising from this consent shall fall under the jurisdiction of Calgary, Alberta, Canada. I agree that under no circumstances any legal claim can be more than the amount charged to me for the rendered services by {companyName}.</p>
              <p className='tjust'>I understand that I may withdraw my consent at any time by submitting a written request; however, doing so may limit {companyName}'s ability to provide certain services.</p>
              <p><strong>The full Privacy Policy is available on the official {companyName} website or upon request.</strong></p>
              <p><strong>Declaration:</strong></p>
              <p className='tjust'>I confirm that I have read and understood this consent form and voluntarily consent to the collection, use, and electronic storage of my personal and health information as described above.</p>
              <p><strong>Date:</strong> {Utility.Format.Date(today,"MMMMM dd, yyyy")}</p>
            </div>
            <SignaturePad signData={signData} setSignData={setSignData} receiverName={receiverName} setReceiverName={setReceiverName} receiverPhone={receiverPhone} setReceiverPhone={setReceiverPhone} />
            
            <Field.PrintedOn fixed={false} />
        </Control.Print.Data>    
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default ConsentPrint11;